import { useState, useEffect, useCallback } from 'react';
import './App.css';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Recipes from './pages/Recipes';
import WeekMenu from './pages/WeekMenu';
import Settings from './pages/Settings';
import LoginPage from './components/LoginPage';
import { useToast, ToastContainer } from './components/Toast';
import { api } from './api';

const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
  { id: 'products',  label: 'Producten',  icon: '📦' },
  { id: 'recipes',   label: 'Recepten',   icon: '🍽️' },
  { id: 'weekmenu',  label: 'Weekmenu',   icon: '📅' },
  { id: 'settings',  label: 'Instellingen', icon: '⚙️' },
];


function isTokenValid() {
  const token  = localStorage.getItem('auth_token');
  const expiry = localStorage.getItem('auth_expiry');
  if (!token || !expiry) return false;
  return Date.now() < parseInt(expiry, 10);
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(isTokenValid);
  const [page, setPage]       = useState('dashboard');
  const [stats, setStats]     = useState(null);
  const [settings, setSettings] = useState({});
  const [theme, setTheme]     = useState(() => localStorage.getItem('theme') || 'dark');
  const [openRecipeId, setOpenRecipeId] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const { toasts } = useToast();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_expiry');
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [handleLogout]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      theme === 'dark' ? '#10131A' : '#F8F9FF'
    );
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/stats').then(setStats).catch(() => {});
    api.get('/settings').then(setSettings).catch(() => {});
    const iv = setInterval(() => api.get('/stats').then(setStats).catch(() => {}), 60000);
    return () => clearInterval(iv);
  }, [isAuthenticated]);

  const refreshStats = () => api.get('/stats').then(setStats).catch(() => {});

  const handleNavigate = (p, recipeId = null) => {
    setOpenRecipeId(recipeId);
    setPage(p);
    refreshStats();
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  const expiredCount  = stats?.expired  || 0;
  const expiringCount = stats?.expiring || 0;

  return (
    <div className="app">
      {/* ── Top App Bar ── */}
      <header className="top-app-bar">
        <div className="top-app-bar-inner">
          <div className="byjonas-brand">
            <svg className="byjonas-mark" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1269 1194" role="img" aria-label="by Jonas">
              <defs>
                <linearGradient id="byjonas-grad" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#A16DDF"/>
                  <stop offset="1" stopColor="#8355C2"/>
                </linearGradient>
              </defs>
              <path d="M328.2,1137.0C193.8,1111.8 83.7,1091.0 83.5,1090.8C83.2,1090.5 13.4,569.1 12.7,562.3C12.5,560.4 302.2,156.2 305.0,154.5C306.2,153.8 986.0,12.0 988.2,12.0C989.6,12.0 990.0,12.8 990.0,15.4C990.0,17.3 990.4,19.0 990.9,19.2C992.6,19.8 1258.0,571.0 1258.0,573.8C1258.0,577.6 1198.3,960.0 1197.6,960.7C1195.8,962.5 579.1,1183.0 576.0,1182.9C574.1,1182.8 462.5,1162.2 328.2,1137.0Z" fill="url(#byjonas-grad)"/>
              <path d="M288.0,938.5C277.0,935.4 268.0,928.0 263.3,918.0C261.0,913.2 260.6,910.6 259.8,896.5C258.3,871.1 259.5,775.6 261.4,764.3C271.1,708.1 312.3,673.9 370.3,674.0C415.7,674.1 450.0,694.5 469.0,732.9C479.7,754.3 480.2,759.6 479.8,844.0C479.5,912.3 479.5,912.5 477.3,917.3C463.5,947.1 418.1,947.4 404.4,917.8L401.5,911.5L401.0,843.0L400.5,774.5L397.7,768.8C391.2,755.6 377.2,749.5 362.0,753.4C353.4,755.6 345.9,760.7 342.3,766.7L339.5,771.5L339.0,841.0L338.5,910.5L335.4,917.0C330.8,926.4 326.1,931.3 317.5,935.6C308.6,940.0 297.4,941.1 288.0,938.5ZM523.3,938.5C513.4,935.4 502.8,926.3 498.7,917.2C493.7,906.0 493.8,778.4 498.9,756.0C506.9,720.8 530.5,693.2 563.0,681.1C591.7,670.4 631.5,672.2 657.5,685.3C683.5,698.4 704.6,724.8 712.7,754.5L715.4,764.5L715.8,835.5C716.2,915.0 716.2,914.7 709.3,923.7C704.4,930.1 701.4,932.5 694.0,936.2C687.9,939.3 687.0,939.5 676.5,939.5C662.9,939.5 655.7,936.6 647.1,927.5C637.6,917.4 637.5,917.4 637.2,880.2L636.9,848.0L605.9,848.0L575.0,848.0L575.0,875.9C575.0,911.4 573.8,917.7 565.3,927.1C555.5,937.9 537.2,942.9 523.3,938.5ZM758.0,939.1C741.8,934.0 731.1,916.9 732.2,897.9C733.0,884.5 741.9,871.6 754.7,865.4L760.5,862.5L814.0,862.0C870.0,861.5 871.1,861.4 872.5,857.1C873.4,854.1 872.0,850.3 869.4,848.6C867.5,847.3 863.1,847.0 843.3,847.0C830.2,847.0 815.5,846.5 810.5,845.9C751.5,838.7 718.8,786.8 737.1,729.5C743.7,708.8 758.4,692.3 779.5,681.9C795.1,674.2 794.9,674.3 863.0,673.9L923.5,673.5L930.2,676.8C955.7,689.3 959.2,728.4 936.3,744.1C925.8,751.3 926.3,751.3 868.0,751.8C809.0,752.2 812.0,751.8 812.0,759.5C812.0,766.5 811.7,766.5 843.4,766.6C874.5,766.8 882.8,767.8 897.1,773.0C923.2,782.5 942.2,804.3 950.2,833.6C953.0,844.2 953.2,863.4 950.6,873.5C944.1,898.5 924.0,923.0 901.6,933.0C887.2,939.5 888.5,939.4 821.5,939.6C788.0,939.8 759.4,939.5 758.0,939.1ZM632.0,765.1C632.0,763.6 623.3,756.1 619.7,754.7C611.5,751.2 598.1,751.9 589.8,756.1C585.6,758.3 579.0,763.7 579.0,765.1C579.0,765.6 590.5,766.0 605.5,766.0C620.1,766.0 632.0,765.6 632.0,765.1ZM403.6,653.0C332.6,646.7 275.5,595.7 263.9,528.2C261.5,513.8 261.3,489.3 263.5,480.5C271.1,450.5 301.6,433.1 332.4,441.0C358.2,447.6 372.1,465.1 376.5,496.2C379.2,515.3 382.7,523.8 391.1,531.4C403.5,542.5 428.9,543.0 446.5,532.5C454.8,527.5 457.6,524.6 461.2,517.2L464.3,510.9L464.5,415.2C464.7,309.1 464.5,311.9 471.3,298.1C493.0,253.8 570.4,264.7 578.0,313.0C579.7,324.4 579.3,508.3 577.5,520.5C570.8,565.0 541.0,610.6 504.0,632.9C478.5,648.3 439.3,656.1 403.6,653.0ZM775.1,644.8C694.8,640.4 623.3,581.2 603.0,502.3C575.5,395.9 643.3,288.8 752.1,266.8C769.9,263.2 807.2,263.4 825.0,267.2C949.0,293.9 1014.3,426.2 959.0,538.5C924.6,608.5 854.1,649.2 775.1,644.8ZM797.3,495.5C825.6,488.1 838.9,456.2 823.6,432.2C802.6,399.0 752.2,409.6 746.0,448.4C741.4,477.3 769.3,502.8 797.3,495.5Z" fill="#FFFFFF"/>
            </svg>
            <span style={{ fontSize: 26, flexShrink: 0 }}>🧊</span>
            <span className="top-app-bar-title">
              <strong>{settings.freezer_name || 'Freezer by Jonas'}</strong>
            </span>
          </div>
          <div className="top-app-bar-actions">
            <button
              className="icon-btn"
              onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Licht thema' : 'Donker thema'}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button
              className="icon-btn"
              onClick={handleLogout}
              title="Uitloggen"
            >
              ↩
            </button>
          </div>
        </div>
      </header>

      {/* ── App Body (rail + content) ── */}
      <div className="app-body">

        {/* Navigation Rail — desktop only */}
        <nav className="nav-rail">
          {NAV.map(n => {
            const badge = n.id === 'products'
              ? (expiredCount > 0 ? expiredCount : expiringCount > 0 ? expiringCount : null)
              : null;
            const badgeWarn = n.id === 'products' && expiredCount === 0 && expiringCount > 0;
            return (
              <button
                key={n.id}
                className={`nav-rail-item ${page === n.id ? 'active' : ''}`}
                onClick={() => handleNavigate(n.id)}
              >
                <div className="nav-rail-indicator" style={{ position: 'relative' }}>
                  <span className="nav-rail-icon">{n.icon}</span>
                  {badge !== null && (
                    <span className={`nav-rail-badge ${badgeWarn ? 'warn' : ''}`}>
                      {badge}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 11, marginTop: 2 }}>{n.label}</span>
              </button>
            );
          })}

          {/* Stats footer in rail */}
          {stats && (
            <div style={{
              marginTop: 'auto',
              padding: '12px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              color: 'var(--md-on-surface-variant)',
              fontSize: 10,
              fontFamily: 'var(--md-font-mono)',
              textAlign: 'center',
              lineHeight: 1.4,
              borderTop: '1px solid var(--md-outline-variant)',
              width: '100%',
            }}>
              <span>{stats.total}</span>
              <span style={{ fontSize: 9, opacity: 0.6 }}>items</span>
            </div>
          )}
        </nav>

        {/* ── Main content ── */}
        <main className="main">
          {page === 'dashboard' && <Dashboard onNavigate={handleNavigate} />}
          {page === 'products'  && (
            <Products
              triggerAdd={showAddProduct}
              onAddTriggered={() => setShowAddProduct(false)}
            />
          )}
          {page === 'recipes'   && (
            <Recipes
              openRecipeId={openRecipeId}
              onRecipeOpened={() => setOpenRecipeId(null)}
            />
          )}
          {page === 'weekmenu'  && <WeekMenu onNavigate={handleNavigate} />}
          {page === 'settings'  && <Settings />}
        </main>
      </div>

      {/* ── Bottom Navigation Bar — mobile only ── */}
      <nav className="bottom-nav">
        <div className="bottom-nav-items">
          {NAV.map(n => {
            const badge = n.id === 'products'
              ? (expiredCount > 0 ? expiredCount : expiringCount > 0 ? expiringCount : null)
              : null;
            const badgeWarn = n.id === 'products' && expiredCount === 0 && expiringCount > 0;
            return (
              <button
                key={n.id}
                className={`bottom-nav-item ${page === n.id ? 'active' : ''}`}
                onClick={() => handleNavigate(n.id)}
              >
                <div className="bottom-nav-indicator" style={{ position: 'relative' }}>
                  <span className="nav-icon-emoji">{n.icon}</span>
                  {badge !== null && (
                    <span className={`nav-badge ${badgeWarn ? 'warn' : ''}`}>
                      {badge}
                    </span>
                  )}
                </div>
                <span className="nav-label">{n.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── FAB — context-sensitive per page ── */}
      {page === 'dashboard' && (
        <button
          className="fab"
          onClick={() => { handleNavigate('products'); setShowAddProduct(true); }}
          title="Product toevoegen"
          aria-label="Product toevoegen"
        >＋</button>
      )}
      {page === 'products' && (
        <button
          className="fab"
          onClick={() => setShowAddProduct(true)}
          title="Product toevoegen"
          aria-label="Product toevoegen"
        >＋</button>
      )}
      {/* Recipes page manages its own speed-dial FAB */}

      <ToastContainer toasts={toasts} />
    </div>
  );
}
